import {
  type KpiPfyPayload,
  type MeanTimeBetweenFailuresResponse,
  type PlannedYearlyMeanTimeBetweenFailuresHierarchy,
  type MtmYtmRequestPayload,
  PERFORMANCE_KPI_REQUEST_STALE_TIME
} from '@domain/kpis'
import {ApiClient} from '@hconnect/apiclient'
import {useUpmPlantsContext} from '@hconnect/upm'
import {useQueries, UseQueryResult} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useCallback, useMemo} from 'react'

import {mapMeanTimeBetweenFailuresDto} from '../../mappers'
import {type KpiHookReturn} from '../../types'

import {
  getMeanTimeBetweenFailures,
  getMeanTimeBetweenFailuresMtmYtmQueryKeys
} from './useMeanTimeBetweenFailures'

export const getMeanTimeBetweenFailuresPfy = async (
  payload?: KpiPfyPayload,
  signal?: AbortSignal
) => {
  const url = '/kpi/dashboard/mean-time-between-failures-pfy'
  const response = await ApiClient.axiosInstance.get<PlannedYearlyMeanTimeBetweenFailuresHierarchy>(
    url,
    {
      params: {...payload},
      signal
    }
  )

  return response.data
}

export const useMeanTimeBetweenFailuresData = (
  payload: MtmYtmRequestPayload,
  enabled = true
): KpiHookReturn => {
  const upmPlants = useUpmPlantsContext()

  const queries = useQueries<
    [
      UseQueryResult<PlannedYearlyMeanTimeBetweenFailuresHierarchy, AxiosError>,
      UseQueryResult<MeanTimeBetweenFailuresResponse, AxiosError>
    ]
  >({
    queries: [
      {
        queryKey: [
          'mean-time-between-failures-pfy',
          {locationId: payload?.locationId, year: payload?.year}
        ],
        queryFn: ({signal}) =>
          getMeanTimeBetweenFailuresPfy(
            {
              locationId: payload?.locationId,
              year: payload?.year
            },
            signal
          ),
        enabled,
        staleTime: PERFORMANCE_KPI_REQUEST_STALE_TIME
      },
      {
        queryKey: getMeanTimeBetweenFailuresMtmYtmQueryKeys(payload),
        queryFn: ({signal}) => getMeanTimeBetweenFailures(payload, signal),
        enabled,
        staleTime: PERFORMANCE_KPI_REQUEST_STALE_TIME
      }
    ]
  })

  const refetch = useCallback(() => {
    queries.forEach(({error, refetch: queryRefetch}) => {
      if (error) {
        void queryRefetch()
      }
    })
  }, [queries])

  const [pfyQuery, dataQuery] = queries

  const meanTimeBetweenFailures = useMemo(() => {
    const pfyData = pfyQuery.data
    const data = dataQuery.data
    return pfyData && data ? mapMeanTimeBetweenFailuresDto(data, upmPlants, pfyData) : undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(pfyQuery.data), JSON.stringify(dataQuery.data)])

  return {
    data: meanTimeBetweenFailures,
    isInitialLoading: pfyQuery.isInitialLoading || dataQuery.isInitialLoading,
    error: pfyQuery.error || dataQuery.error,
    refetch
  }
}
